/**
 * The definition of the page routes.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

export enum AppRouteUrl {
  ApiKeyCreate = '/api-keys/create',
  ApiKey = '/api-keys',
  ChangePassword = '/change-password',
  ForgotPassword = '/forgot-password',
  Home = '/',
  GeneralTermsConditions = '/general-terms-conditions',
  LogIn = '/login',
  MyAccount = '/my-account',
  PrivatePolicy = '/ai-use-transparency-framework',
  SetPassword = '/set-password',
  Settings = '/settings',
  SubscriptionSelection = '/tierselection',
  TaskCreation = '/job-creation',
  TaskHistory = '/jobs',
  Team = '/team',
  ThankYou = '/thank-you',
  Usage = '/usage',
  WebhookCreate = '/webhook/create',
  WebhookTest = '/webhook/:webhookId/test',
  WelcomeSetPassword = '/welcome-set-password',
}

const AppRouteTitleList: Record<string, string> = {
  [AppRouteUrl.ApiKeyCreate]: 'Create API Credentials',
  [AppRouteUrl.ApiKey]: 'API Keys',
  [AppRouteUrl.ChangePassword]: 'Change password',
  [AppRouteUrl.ForgotPassword]: 'Forgot Password',
  [AppRouteUrl.Home]: 'Home',
  [AppRouteUrl.GeneralTermsConditions]: 'General Terms & Conditions',
  [AppRouteUrl.MyAccount]: 'My Account',
  [AppRouteUrl.LogIn]: 'Log In',
  [AppRouteUrl.PrivatePolicy]: 'AI Use & Transparency Framework',
  [AppRouteUrl.SetPassword]: 'Set Password',
  [AppRouteUrl.Settings]: 'Settings',
  [AppRouteUrl.SubscriptionSelection]: 'Subscription Selection',
  [AppRouteUrl.TaskCreation]: 'Create A New Task',
  [AppRouteUrl.TaskHistory]: 'Task History',
  [AppRouteUrl.Team]: 'Team',
  [AppRouteUrl.ThankYou]: 'Thank you',
  [AppRouteUrl.Usage]: 'Usage',
  [AppRouteUrl.WebhookCreate]: 'Create A Webhook',
  [AppRouteUrl.WebhookTest]: 'Webhook Test',
  [AppRouteUrl.WelcomeSetPassword]: 'Welcome to Ircam Amplify',
}

/**
 * Return title of route.
 * @param route The requested route.
 * @returns The message or null if not found.
 */
export function getRouteTitle(route?: AppRouteUrl | string): string | null {
  if (!route) {
    return null
  }

  if (route in AppRouteTitleList) {
    return AppRouteTitleList[route]
  }

  // Route not found: try to convert routes with arguments like `{0}`
  // into a RegEx pattern.
  const match = Object.entries(AppRouteTitleList).find(([pattern]) =>
    patternToRegExp(pattern).test(route)
  )

  return match ? match[1] : null
}

/**
 * Turn a route template such as `/webhooks/{0}/logs` into a matching
 * regular expression, where each `{n}` placeholder matches one path
 * segment.
 */
function patternToRegExp(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.*+?^$()|[\]\\{}]/g, '\\$&')
    .replace(/\\\{\d+\\\}/g, '[^/]+')

  return new RegExp(`^${escaped}$`)
}
